/**
 * Search slice — Redux Toolkit state, thunks, and selectors for storefront
 * product search and category navigation. SF-SEARCH-001 / SF-SEARCH-002.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { searchProducts as apiSearchProducts } from '@dfq/shared/api/products';
import { getCategories, type CategoryTree } from '@dfq/shared/api/categories';
import { isApiError } from '@dfq/shared/api/_envelope';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Preserved platform error envelope — mirrors the ApiResponse error shape. */
export interface SearchErrorEnvelope {
  message: string;
  severity?: 'info' | 'warning' | 'error' | 'critical';
  errors?: Record<string, string[]>;
  errorCode?: string;
  status?: number;
}

export type SortOrder = 'featured' | 'price_asc' | 'price_desc' | 'newest';

export interface BreadcrumbItem {
  label: string;
  href: string;
}

export interface FilterState {
  material: string[];
  vehicleType: string[];
  priceMin: number | null;
  priceMax: number | null;
}

/**
 * Condensed product shape returned by `GET /api/v1/products` search results.
 * Mirrors the wire shape described in the ticket's API contract; SF-SEARCH-002
 * will reconcile this against the platform DTO when the endpoint ships.
 */
export interface SearchProductResult {
  id: number;
  name: string;
  slug: string;
  categorySlug: string | null;
  primaryImageUrl: string | null;
  basePrice: string;
  basePriceUnit: string;
  isFeatured: boolean;
  materials: string[];
}

/**
 * Nested category node from `GET /api/v1/categories`.
 * Mirrors the wire shape in the ticket's API contract.
 */
export interface SearchCategoryNode {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  imagePath: string | null;
  sortOrder: number;
  isVisible: boolean;
  /** Lucide icon name (e.g. `"car"`), or `null` when unset. SF-HOME-102. */
  icon: string | null;
  children: SearchCategoryNode[];
}

export interface SearchState {
  query: string;
  results: SearchProductResult[];
  categoryTree: SearchCategoryNode[];
  totalCount: number;
  currentPage: number;
  filters: FilterState;
  sortOrder: SortOrder;
  loading: boolean;
  error: SearchErrorEnvelope | null;
  activeCategorySlug: string | null;
  breadcrumbs: BreadcrumbItem[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_FILTERS: FilterState = {
  material: [],
  vehicleType: [],
  priceMin: null,
  priceMax: null,
};

// ---------------------------------------------------------------------------
// Wire-shape (internal — not exported)
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Thunk params
// ---------------------------------------------------------------------------

export interface SearchProductsParams {
  query: string;
  filters: FilterState;
  sortOrder: SortOrder;
  page: number;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: SearchState = {
  query: '',
  results: [],
  categoryTree: [],
  totalCount: 0,
  currentPage: 1,
  filters: DEFAULT_FILTERS,
  sortOrder: 'featured',
  loading: false,
  error: null,
  activeCategorySlug: null,
  breadcrumbs: [],
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const searchProducts = createAsyncThunk<
  { results: SearchProductResult[]; totalCount: number; currentPage: number },
  SearchProductsParams,
  { rejectValue: SearchErrorEnvelope }
>(
  'search/searchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const data = await apiSearchProducts({
        q: params.query || undefined,
        material: params.filters.material,
        vehicleType: params.filters.vehicleType,
        priceMin: params.filters.priceMin,
        priceMax: params.filters.priceMax,
        sortOrder: params.sortOrder !== 'featured' ? params.sortOrder : undefined,
        page: params.page > 1 ? params.page : undefined,
      });

      const results: SearchProductResult[] = data.items.map((item) => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        categorySlug: item.category_slug,
        primaryImageUrl: item.primary_image_url,
        basePrice: item.base_price,
        basePriceUnit: item.base_price_unit,
        isFeatured: item.is_featured,
        materials: item.materials,
      }));

      return { results, totalCount: data.total, currentPage: data.current_page };
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        });
      }
      return rejectWithValue({ message: err instanceof Error ? err.message : 'Search failed', severity: 'error' });
    }
  },
);

export const fetchCategoryTree = createAsyncThunk<
  SearchCategoryNode[],
  void,
  { rejectValue: SearchErrorEnvelope }
>(
  'search/fetchCategoryTree',
  async (_, { rejectWithValue }) => {
    try {
      const wireNodes = await getCategories();

      function mapNode(wire: CategoryTree): SearchCategoryNode {
        return {
          id: wire.id,
          name: wire.name,
          slug: wire.slug,
          description: wire.description,
          imagePath: wire.imagePath,
          sortOrder: wire.sortOrder,
          isVisible: wire.isVisible,
          icon: wire.icon ?? null,
          children: wire.children.map(mapNode),
        };
      }

      return wireNodes.map(mapNode);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        });
      }
      return rejectWithValue({ message: err instanceof Error ? err.message : 'Failed to load categories', severity: 'error' });
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },

    setFilters(state, action: PayloadAction<Partial<FilterState>>) {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },

    setSortOrder(state, action: PayloadAction<SortOrder>) {
      state.sortOrder = action.payload;
      state.currentPage = 1;
    },

    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },

    setActiveCategorySlug(state, action: PayloadAction<string | null>) {
      state.activeCategorySlug = action.payload;
    },

    setBreadcrumbs(state, action: PayloadAction<BreadcrumbItem[]>) {
      state.breadcrumbs = action.payload;
    },

    // Preserves activeCategorySlug and breadcrumbs — nav context survives a search clear.
    clearSearch(state) {
      state.query = '';
      state.results = [];
      state.totalCount = 0;
      state.currentPage = 1;
      state.filters = DEFAULT_FILTERS;
    },
  },
  extraReducers(builder) {
    builder
      // -- searchProducts ------------------------------------------------
      .addCase(searchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.results;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? { message: action.error.message ?? 'Search failed', severity: 'error' };
      })
      // -- fetchCategoryTree ---------------------------------------------
      .addCase(fetchCategoryTree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.loading = false;
        state.categoryTree = action.payload;
      })
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? { message: action.error.message ?? 'Failed to load categories', severity: 'error' };
      });
  },
});

export const {
  setQuery,
  setFilters,
  setSortOrder,
  setPage,
  setActiveCategorySlug,
  setBreadcrumbs,
  clearSearch,
} = searchSlice.actions;

export const searchReducer = searchSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

interface StateWithSearch {
  search: SearchState;
}

export const selectSearchResults = (state: StateWithSearch): SearchProductResult[] =>
  state.search.results;

export const selectSearchFilters = (state: StateWithSearch): FilterState => state.search.filters;

export const selectSearchLoading = (state: StateWithSearch): boolean => state.search.loading;

export const selectSearchError = (state: StateWithSearch): SearchErrorEnvelope | null =>
  state.search.error;

/** Full category tree from `fetchCategoryTree`, `[]` until it resolves. */
export const selectCategoryTree = (state: StateWithSearch): SearchCategoryNode[] =>
  state.search.categoryTree;

const selectBreadcrumbs = (state: StateWithSearch): BreadcrumbItem[] => state.search.breadcrumbs;
const selectActiveCategorySlug = (state: StateWithSearch): string | null =>
  state.search.activeCategorySlug;

// Memoised — returns breadcrumbs only when activeCategorySlug is set.
export const selectActiveCategoryBreadcrumb = createSelector(
  [selectBreadcrumbs, selectActiveCategorySlug],
  (breadcrumbs, slug): BreadcrumbItem[] => (slug !== null ? breadcrumbs : []),
);
